// Curated recipe corpus. Recommendations rank and adapt these within safe bounds (research C4).
// The one exception is a dish someone asks Amari for that the collection lacks: create-recipe
// writes it in this same format, it is labelled as unreviewed, and it is kept on that device only
// (generated.ts); getRecipe and the steps below include those. Ingredients reference canonical taxonomy ids, so diet and
// allergen violations are computed (diet.ts), never hand-tagged.
//
// Approved recipes live in catalog/catalog.json, validated by catalog/schema.ts at load. Drafts
// live in catalog/drafts.json, which only tooling reads (catalog/all.ts), so they never ship in
// the app bundle; approving a draft moves it across (npm run catalog:approve). `tested` stays false until a recipe has been cooked and signed off in a
// real kitchen, and the UI says so honestly.

import raw from './catalog/catalog.json';
import { catalogSchema, type CATEGORIES, type UNITS } from './catalog/schema';
import { toShipped } from './catalog/ship';
import { generatedRecipe } from './generated';
import { steps as inlineSteps, url as stepsUrl } from 'virtual:recipe-steps';
import type { RecipeMeta } from './recipeMeta';
import type { IngredientId } from './taxonomy';

export type Unit = (typeof UNITS)[number];

/**
 * core    , the dish depends on it; must be in the kitchen (or substituted)
 * pantry  , a seasoning/basic; satisfied by the user's confirmed staples
 * optional, garnish / nice-to-have; never blocks "makeable", still diet-checked
 */
export type IngredientRole = 'core' | 'pantry' | 'optional';

export interface RecipeIngredient {
  id: IngredientId;
  qty?: number;
  unit?: Unit;
  role: IngredientRole;
  /** Measured already cooked (e.g. day-old rice); nutrition uses the cooked USDA record. */
  cooked?: true;
}

export interface RecipeStep {
  text: string;
  timerMin?: number;
  /** Food-safety note, always shown, never stripped by adaptation. */
  safety?: string;
}

export type MealType = 'main' | 'breakfast' | 'side' | 'snack';
export type RecipeTag = 'one_pot' | 'budget' | 'high_protein' | 'sheet_pan' | 'no_cook';

/** A recipe as the domain logic reads it. Steps aren't here: the app loads them on demand (`loadSteps`). */
export interface Recipe {
  id: string;
  title: string;
  minutes: number;
  difficulty: 'easy' | 'medium';
  servings: number;
  meal: MealType;
  ingredients: RecipeIngredient[];
  tags: RecipeTag[];
  tested: boolean;
}

export interface RecipeProvenance {
  source: 'house' | 'adapted' | 'traditional';
  origin?: string;
  localNames?: string[];
  references: { title: string; url: string; accessed: string }[];
  attributionConfidence?: 'high' | 'medium' | 'low';
  adaptationNotes?: string;
}

export type RecipeReview =
  | { status: 'draft'; notes?: string }
  | { status: 'approved'; reviewedBy: string; reviewedAt: string; notes?: string };

export type Category = (typeof CATEGORIES)[number];

/** A full catalog record, for tooling: the recipe with its steps, metadata, provenance and review. */
export interface CatalogEntry extends Recipe {
  /** Planning group from the 500-meal plan, used to browse by region. */
  category: Category;
  steps: RecipeStep[];
  meta: RecipeMeta;
  provenance: RecipeProvenance;
  review: RecipeReview;
}

/** A catalog record without its steps: what the image tooling reads, and the base of what the app ships. */
export type CatalogRecord = Omit<CatalogEntry, 'steps'>;

/** What the app holds for an approved recipe (see catalog/ship.ts): no steps, just how many. */
export interface ShippedRecipe extends CatalogRecord {
  stepCount: number;
}

/**
 * Recipes the app may show. catalog.json holds approved entries only. The app build validates and
 * trims it at build time (vite.config.ts, prevalidatedCatalog), so a launch doesn't re-check 395
 * recipes; dev and tests validate it here.
 */
const shipped = raw as unknown as { prevalidated?: true; recipes: ShippedRecipe[] };
export const RECIPES: ShippedRecipe[] = shipped.prevalidated
  ? shipped.recipes
  : catalogSchema.parse(raw).recipes.map(toShipped);
if (RECIPES.some((r) => r.review.status !== 'approved')) throw new Error('catalog.json holds only approved recipes; drafts go in drafts.json');

const BY_ID = new Map(RECIPES.map((r) => [r.id, r]));
export function getRecipe(id: string): ShippedRecipe | undefined {
  return BY_ID.get(id) ?? generatedRecipe(id)?.recipe;
}

let allSteps: Promise<Record<string, RecipeStep[]>> | null = null;
let loaded: Record<string, RecipeStep[]> | null = null;

async function fetchSteps(): Promise<Record<string, RecipeStep[]>> {
  if (inlineSteps) return inlineSteps;
  const res = await fetch(stepsUrl!);
  if (!res.ok) throw new Error(`recipe steps: HTTP ${res.status}`);
  return (await res.json()) as Record<string, RecipeStep[]>;
}

/**
 * A recipe's steps, loaded with every other recipe's on first use (one small file, which the
 * service worker caches for offline use). A failed load isn't kept, so the next call tries again.
 */
export function loadSteps(id: string): Promise<RecipeStep[]> {
  const written = generatedRecipe(id);
  if (written) return Promise.resolve(written.steps);
  allSteps ??= fetchSteps()
    .then((all) => (loaded = all))
    .catch((e: unknown) => { allSteps = null; throw e; });
  return allSteps.then((all) => all[id] ?? []);
}

/** The steps if they've already loaded, so a screen opened again doesn't flash a placeholder. */
export function stepsIfLoaded(id: string): RecipeStep[] | undefined {
  const written = generatedRecipe(id);
  if (written) return written.steps;
  return loaded ? loaded[id] ?? [] : undefined;
}
